import { motion } from "motion/react";
import {
  Heart,
  MessageCircle,
  Play,
  Search,
  Target,
  Users,
  Video,
} from "lucide-react";

import { Badge } from "../ui/badge";
import {
  CalloutBox,
  EnhancedListItem,
  KeyInsights,
  ProjectHeader,
  ProjectSection,
  QuoteBox,
  StatsGrid,
  TwoColumnLayout,
} from "./Components";
import { ProjectPageLayout } from "./ProjectPageLayout";

// Image imports
import ChillLaptop from "../../utils/projects/Chill/ChillLaptop.png";
import CompetiveAnalysis from "../../utils/projects/Chill/Competive.png";
import FinalHome from "../../utils/projects/Chill/FinalHome.png";
import FinalProfile from "../../utils/projects/Chill/FinalProfile.png";
import FinalText from "../../utils/projects/Chill/WatchFinal2.png";
import FinalView from "../../utils/projects/Chill/FinalView1.png";
import FinalWatch from "../../utils/projects/Chill/WatchFinal1.png";
import SocialMedia from "../../utils/projects/Chill/SocialMedia.png";
import V10 from "../../utils/projects/Chill/V1.0.png";
import V11 from "../../utils/projects/Chill/V1.1.png";

export const ChillProjectPage = () => {
  return (
    <ProjectPageLayout
      projectId="chill"
      titleOverride="&Chill"
      headerMaxWidthClassName="max-w-4xl"
    >
      {/* Overview Image */}
      <ProjectSection id="overview">
        <div className="bg-gradient-to-br from-blue-100/80 to-cyan-100/80 rounded-3xl p-8 backdrop-blur-sm">
          <motion.img
            src={ChillLaptop}
            alt="&Chill Project Overview"
            className="w-full rounded-2xl shadow-2xl mb-8"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          />
        </div>
      </ProjectSection>

      {/* TL;DR Section */}
      <ProjectSection id="tldr" className="space-y-6">
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-blue-200">
          <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-3">
            TL;DR
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">📺</span>
                <h4 className="font-bold text-sm">The Problem</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                Existing co-viewing platforms require plug-ins, crash
                frequently, and lack integrated video chat
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🎬</span>
                <h4 className="font-bold text-sm">The Solution</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                Native streaming platform with integrated video chat, social
                features and recommendation algorithm
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🎨</span>
                <h4 className="font-bold text-sm">My Role</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                Product designer conducting user research, competitive analysis,
                and end-to-end UI/UX design
              </p>
            </div>
          </div>
        </div>
      </ProjectSection>

      {/* Problem & Solution Section */}
      <ProjectSection id="about" className="space-y-6">
        <ProjectHeader label="Problem & Solution" />

        <div className="grid md:grid-cols-2 gap-6">
          {/* Problem */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-red-50/80 to-orange-50/80 rounded-2xl p-6 border border-red-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                <span className="text-white text-xl">⚠️</span>
              </div>
              <h3 className="font-bold text-lg">The Problem</h3>
            </div>
            <div className="space-y-3 text-sm leading-relaxed text-gray-700">
              <p>
                <strong>
                  Co-viewing extensions like Netflix Party fall short.
                </strong>{" "}
                Users face:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>
                    <strong>Technical barriers:</strong> Everyone must download
                    plug-ins that frequently crash
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>
                    <strong>Setup friction:</strong> Links break, browsers
                    crash, setup takes 10+ minutes
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>
                    <strong>No video chat:</strong> Users resort to FaceTime on
                    separate devices
                  </span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Solution */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-green-50/80 to-emerald-50/80 rounded-2xl p-6 border border-green-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                <span className="text-white text-xl">✅</span>
              </div>
              <h3 className="font-bold text-lg">Our Solution</h3>
            </div>
            <div className="space-y-3 text-sm leading-relaxed text-gray-700">
              <p>
                <strong>&Chill</strong> is a native streaming platform built for
                social viewing:
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="font-bold text-green-700">🎥</span>
                  <span>
                    <strong>Integrated video chat</strong>—no separate apps or
                    devices needed, just invite your friends to a watch party
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-green-700">⚡</span>
                  <span>
                    <strong>Social activity feed</strong>—see what your friends
                    are watching in real-time
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-green-700">🔒</span>
                  <span>
                    <strong>Recommendation algorithm</strong>—get personalized
                    recommendations based on your friends' streaming activity
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </ProjectSection>

      {/* User Research Section */}
      <ProjectSection id="research">
        <ProjectHeader label="User Research" />

        <CalloutBox title="Research Approach">
          <p className=" leading-relaxed">
            I interviewed <strong>Netflix Party users</strong> to understand
            their pain points with existing co-viewing solutions. The goal:
            identify what prevents seamless social streaming.
          </p>
        </CalloutBox>

        <div className="my-12">
          <h3 className="text-2xl font-bold mb-6">What Users Told Me</h3>

          <div className="space-y-4">
            <QuoteBox quote="I love Netflix Partying with my friends but it's really difficult to set up and takes a while. Everyone has to download the extension and the link has to work properly for everyone to be on the same show. If it was easier to do we would use it more." />

            <QuoteBox quote="I like Netflix Party, but my biggest frustration is the lack of a video component. You have to put Facetime on a separate device in order to watch together" />

            <QuoteBox quote="The extension almost always crashed my browser or wouldn't work at all. If anything, Zoom's features felt more communal by allowing everyone in the party to see each other while watching their movie/show" />
          </div>
        </div>

        <div className="space-y-6">
          <h3 className="text-2xl font-bold mb-4">Key Insights</h3>

          <EnhancedListItem
            index={0}
            title="Setup Friction Kills Spontaneity"
            description="Users want to watch together more often, but 10-15 minute setup times (download extension → share link → troubleshoot sync issues) prevent spontaneous co-viewing. Complexity reduces frequency of use."
          />

          <EnhancedListItem
            index={1}
            title="Video Chat is Essential, Not Optional"
            description="All interviewees mentioned using FaceTime or Zoom on separate devices—a clear signal that seeing friends' faces matters. Co-viewing without video feels incomplete."
          />

          <EnhancedListItem
            index={2}
            title="Technical Failures Erode Trust"
            description="Browser crashes and sync failures aren't just annoying—they break the social experience. Users lose trust in extensions and avoid using them for important watch sessions (finales, premieres)."
          />
        </div>
      </ProjectSection>

      {/* Competitive Analysis */}
      <ProjectSection id="competitive">
        <ProjectHeader label="Competitive Analysis" />

        <TwoColumnLayout
          left={
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Search className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold">Streaming Platforms</h3>
              </div>
              <p className="text-sm leading-relaxed mb-4 text-gray-700">
                Analyzed <strong>Netflix, Prime Video, HBO, and Hulu</strong>{" "}
                for content discovery patterns and UI conventions
              </p>
              <img
                src={CompetiveAnalysis}
                alt="Competitive Analysis"
                className="w-full rounded-xl shadow-lg"
              />
            </div>
          }
          right={
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-6 h-6 text-purple-600" />
                <h3 className="text-xl font-bold">Social Platforms</h3>
              </div>
              <p className="text-sm leading-relaxed mb-4 text-gray-700">
                Studied <strong>Snapchat, HouseParty, and Spotify</strong> for
                engagement mechanics and social features
              </p>
              <img
                src={SocialMedia}
                alt="Social Media Analysis"
                className="w-full rounded-xl shadow-lg"
              />
            </div>
          }
        />

        <KeyInsights
          title="Competitive Gaps Discovered"
          insights={[
            "No major streaming platform offers integrated video chat—users rely on third-party solutions",
            "Most platforms require shared subscriptions for co-viewing, creating artificial barriers",
            "Existing solutions require manual link sharing rather than in-app friend invites",
            "Privacy controls are either absent (anyone can join) or overly restrictive (closed groups only)",
          ]}
        />

        <CalloutBox title="Social Platform Patterns">
          <p className="leading-relaxed mb-4">
            I identified <strong>3 successful engagement mechanics</strong> from
            social platforms that could translate to streaming:
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2 text-sm">📸 Snapchat Streaks</h4>
              <p className="text-xs text-gray-600">
                Gamification creates habit loops and encourages daily engagement
              </p>
            </div>
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2 text-sm">🎵 Spotify Sharing</h4>
              <p className="text-xs text-gray-600">
                Real-time activity visibility (what friends are listening to
                now)
              </p>
            </div>
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2 text-sm">🏠 HouseParty Rooms</h4>
              <p className="text-xs text-gray-600">
                Drop-in video rooms with friendly "wave" invites
              </p>
            </div>
          </div>
        </CalloutBox>
      </ProjectSection>

      {/* Design Questions Section */}
      <ProjectSection id="questions">
        <ProjectHeader label="Reframing the Challenge" />

        <CalloutBox title="Core Insight">
          <p className="text-xl font-semibold text-center">
            "Users <strong>want</strong> communal digital viewing experiences,
            but <strong>communication and technical barriers</strong> prevent
            adoption."
          </p>
        </CalloutBox>

        <p className="text-center text-gray-600 my-8">
          This insight led me to ask three key design questions:
        </p>

        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-blue-100/80 to-cyan-100/80 rounded-2xl p-6 backdrop-blur-sm"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl mt-1">🔗</span>
              <div>
                <h4 className="font-bold mb-2">
                  How might we eliminate setup friction?
                </h4>
                <p className="text-sm leading-relaxed text-gray-700">
                  Simplify inviting friends and synchronizing playback across
                  devices—no plug-ins, no link sharing, no troubleshooting
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-gradient-to-br from-purple-100/80 to-pink-100/80 rounded-2xl p-6 backdrop-blur-sm"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl mt-1">👥</span>
              <div>
                <h4 className="font-bold mb-2">
                  How might we balance solo and social viewing?
                </h4>
                <p className="text-sm leading-relaxed text-gray-700">
                  Enable seamless transitions between individual and group
                  streaming—preserving personalization while enhancing social
                  interaction
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-gradient-to-br from-green-100/80 to-emerald-100/80 rounded-2xl p-6 backdrop-blur-sm"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl mt-1">🎬</span>
              <div>
                <h4 className="font-bold mb-2">
                  How might we replicate in-person movie nights?
                </h4>
                <p className="text-sm leading-relaxed text-gray-700">
                  Capture the shared excitement and real-time reactions of
                  watching together on a couch—through an online platform
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </ProjectSection>

      {/* Design Process Section */}
      <ProjectSection id="design">
        <ProjectHeader label="Design Process" />

        <CalloutBox title="Design Strategy">
          <p className="leading-relaxed">
            After brainstorming on paper, I created{" "}
            <strong>V1 prototypes in Figma</strong>. I drew inspiration from
            existing streaming platforms for familiar layout patterns, then
            layered in social features from HouseParty and Spotify. I chose a{" "}
            <strong>dark theme</strong> since most users stream in low-light
            environments.
          </p>
        </CalloutBox>

        <div className="my-12">
          <h3 className="text-2xl font-bold mb-6">Iterative Prototyping</h3>

          <TwoColumnLayout
            left={
              <div>
                <h4 className="font-bold mb-4">V1.0 - Core Layout</h4>
                <img
                  src={V10}
                  alt="V1.0 Prototype"
                  className="w-full rounded-xl shadow-lg mb-4"
                />
                <p className="text-sm text-gray-600">
                  Established familiar streaming interface with content browsing
                  and recommendations
                </p>
              </div>
            }
            right={
              <div>
                <h4 className="font-bold mb-4">V1.1 - Social Layer</h4>
                <img
                  src={V11}
                  alt="V1.1 Prototype"
                  className="w-full rounded-xl shadow-lg mb-4"
                />
                <p className="text-sm text-gray-600">
                  Added right-hand activity panel showing friends' streaming
                  status and join requests
                </p>
              </div>
            }
          />
        </div>

        <CalloutBox title="Initial Social Features">
          <p className="leading-relaxed">
            Inspired by <strong>Spotify's "Friend Activity"</strong> and{" "}
            <strong>HouseParty's drop-in rooms</strong>, V1.1 included a
            right-hand panel showing friends' streaming activity. Users could
            request to join <strong>public watch parties</strong>—but this
            raised privacy concerns in testing.
          </p>
        </CalloutBox>

        <div className="my-12">
          <h3 className="text-2xl font-bold mb-6">User Testing Insights</h3>

          <div className="space-y-6">
            <EnhancedListItem
              index={0}
              title="Privacy Over Spontaneity"
              description="Two-thirds of participants wanted private watch parties by default. The fear: spontaneous streaming could feel chaotic if anyone could join uninvited. Users valued control over who enters their viewing space."
            />

            <EnhancedListItem
              index={1}
              title="Video Chat Concerns in Large Groups"
              description="While users loved the integrated video chat idea, some worried it would become distracting in parties of 5+ people. This validated limiting party size and providing toggle controls."
            />

            <EnhancedListItem
              index={2}
              title="Unclear Invitation Flow"
              description="The concept of 'sending an invite' was vague. Users asked: Does it send a notification? An email? What does the recipient see? This revealed the need for clearer invitation UX and real-time status indicators."
            />
          </div>

          <CalloutBox title="Design Pivots">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-xl">→</span>
                <p className="text-sm">
                  <strong>Privacy by default:</strong> Watch parties are private
                  unless explicitly made public
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-xl">→</span>
                <p className="text-sm">
                  <strong>Limited party size:</strong> Cap at 5 friends to
                  prevent overwhelming group dynamics
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-xl">→</span>
                <p className="text-sm">
                  <strong>Clear invitation UI:</strong> Show real-time status
                  (pending, accepted, declined) for transparency
                </p>
              </div>
            </div>
          </CalloutBox>
        </div>
      </ProjectSection>

      {/* Final Product Section */}
      <ProjectSection id="final">
        <ProjectHeader label="Final Product" />

        <div className="space-y-16">
          {/* Home Page */}
          <TwoColumnLayout
            left={
              <div>
                <img
                  src={FinalHome}
                  alt="Final Home Page"
                  className="w-full rounded-xl shadow-lg"
                />
              </div>
            }
            right={
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Play className="w-6 h-6 text-purple-600" />
                  <h3 className="text-xl font-bold">Home Page</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <p className="leading-relaxed text-gray-700">
                    <strong>Friend activity sidebar</strong> shows who's
                    watching what right now—making it easy to join ongoing watch
                    parties
                  </p>
                  <p className="leading-relaxed text-gray-700">
                    <strong>Personalized recommendations</strong> prominently
                    featured, with "Watch with Friends" suggestions based on
                    mutual interests
                  </p>
                  <p className="leading-relaxed text-gray-700">
                    <strong>Privacy maintained:</strong> Only shows activity for
                    parties users have been explicitly invited to
                  </p>
                </div>
              </div>
            }
          />

          {/* Profile Page */}
          <TwoColumnLayout
            left={
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-6 h-6 text-purple-600" />
                  <h3 className="text-xl font-bold">Profile Page</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <p className="leading-relaxed text-gray-700">
                    Users curate <strong>favorite shows</strong> visible to
                    friends—creating conversation starters and shared interests
                  </p>
                  <p className="leading-relaxed text-gray-700">
                    When visiting a friend's profile, see{" "}
                    <strong>compatibility recommendations</strong>: "You both
                    love sci-fi—try watching Foundation together"
                  </p>
                  <p className="leading-relaxed text-gray-700">
                    <strong>One-click invites</strong> directly from profile to
                    start a watch party based on shared interests
                  </p>
                </div>
              </div>
            }
            right={
              <div>
                <img
                  src={FinalProfile}
                  alt="Final Profile Page"
                  className="w-full rounded-xl shadow-lg"
                />
              </div>
            }
          />

          {/* Video Page */}
          <TwoColumnLayout
            left={
              <div>
                <img
                  src={FinalView}
                  alt="Final Video Page"
                  className="w-full rounded-xl shadow-lg"
                />
              </div>
            }
            right={
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Video className="w-6 h-6 text-green-600" />
                  <h3 className="text-xl font-bold">Video Page</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <p className="leading-relaxed text-gray-700">
                    <strong>Instant invites:</strong> Select up to 5 active
                    friends when starting a video—no links, no setup
                  </p>
                  <p className="leading-relaxed text-gray-700">
                    <strong>Real-time status:</strong> See who's accepted,
                    pending, or declined immediately
                  </p>
                  <p className="leading-relaxed text-gray-700">
                    <strong>Smart friend list:</strong> Shows only friends
                    currently online and available to watch
                  </p>
                </div>
              </div>
            }
          />

          {/* Watch Party Experience */}
          <div>
            <h3 className="text-2xl font-bold mb-6">
              The Watch Party Experience
            </h3>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-gradient-to-br from-purple-100/80 to-pink-100/80 rounded-2xl p-6 backdrop-blur-sm"
              >
                <img
                  src={FinalWatch}
                  alt="Watch Party Interface"
                  className="w-full rounded-xl shadow-lg mb-4"
                />
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">🎬</span>
                  <h4 className="font-bold">Synchronized Playback</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Host controls playback—pauses, rewinds, and skips sync
                  instantly for everyone
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-gradient-to-br from-blue-100/80 to-cyan-100/80 rounded-2xl p-6 backdrop-blur-sm"
              >
                <img
                  src={FinalText}
                  alt="Chat Interface"
                  className="w-full rounded-xl shadow-lg mb-4"
                />
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">💬</span>
                  <h4 className="font-bold">Dual Communication</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Toggle between live text chat and video chat—users choose
                  their preferred interaction mode
                </p>
              </motion.div>
            </div>

            <CalloutBox title="Integrated Communication">
              <p className="leading-relaxed">
                The right panel houses both{" "}
                <strong>text chat and video chat tabs</strong>—solving the #1
                user complaint about Netflix Party. No need for FaceTime on a
                separate device. Users can <strong>toggle between modes</strong>{" "}
                based on context: video for reactions during key scenes, text
                for quieter commentary.
              </p>
            </CalloutBox>
          </div>
        </div>
      </ProjectSection>

      {/* Reflection Section */}
      <ProjectSection id="reflection">
        <ProjectHeader label="Reflection & Learnings" />

        <CalloutBox title="What I Learned">
          <div className="space-y-4">
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2">
                🎯 User Quotes Reveal Pain, Not Just Preferences
              </h4>
              <p className="text-sm leading-relaxed">
                "The extension crashed" isn't just feedback—it's a trust issue.
                Users won't rely on tools that fail during important moments
                (finales, watch parties with distant friends). Reliability isn't
                a feature; it's the foundation.
              </p>
            </div>
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2">
                🔄 Testing Validates Assumptions (and Breaks Them)
              </h4>
              <p className="text-sm leading-relaxed">
                I assumed users would love public watch parties for spontaneity.
                Testing revealed the opposite: 2/3 wanted privacy by default.
                This taught me that "social" doesn't always mean
                "open"—controlled sharing can be more social than unrestricted
                access.
              </p>
            </div>
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2">
                🎨 Competitive Analysis Isn't Just Feature Comparison
              </h4>
              <p className="text-sm leading-relaxed">
                Studying Spotify and Snapchat wasn't about copying features—it
                was about understanding engagement mechanics (streaks, real-time
                activity, friend status). The best insights come from adjacent
                industries, not direct competitors.
              </p>
            </div>
          </div>
        </CalloutBox>
      </ProjectSection>
    </ProjectPageLayout>
  );
};
